import type { Route } from '@atlassian/react-resource-router';
import { getCapabilityMetadataHeaders } from '@jira/platform__app-framework-ufo-capability-header/src/getCapabilityMetadataHeaders.tsx';
import { CAPABILITY_LOAD_TYPE } from '@jira/platform__observability-capabilities/src/constants.tsx';

type RouteEntryPoint = NonNullable<ReturnType<NonNullable<Route['entryPoint']>>>;
type EntryPointQuery = NonNullable<
	ReturnType<RouteEntryPoint['getPreloadProps']>['queries']
>[string];

type EntryPointPreloadProps = {
	readonly entryPoints?: Readonly<
		Record<
			string,
			| {
					readonly entryPoint: EntryPointLike | undefined;
					readonly entryPointParams: unknown;
			  }
			| null
			| undefined
		>
	>;
	readonly queries?: Readonly<Record<string, EntryPointQuery | null | undefined>>;
};

type EntryPointLike = {
	readonly getPreloadProps: (params: never) => EntryPointPreloadProps;
};

const patchQueryCapability = (query: EntryPointQuery | null | undefined, capability: string) => {
	if (query == null) {
		return query;
	}

	const networkCacheConfig = query.options?.networkCacheConfig;
	const headers = getCapabilityMetadataHeaders(
		networkCacheConfig?.metadata?.headers,
		capability,
		CAPABILITY_LOAD_TYPE.PREFETCH,
	);

	if (headers === undefined) {
		return query;
	}

	return {
		...query,
		options: {
			...query.options,
			networkCacheConfig: {
				...networkCacheConfig,
				metadata: {
					...networkCacheConfig?.metadata,
					headers,
				},
			},
		},
	};
};

const patchEntryPointCapability = <TEntryPoint extends EntryPointLike>(
	entryPoint: TEntryPoint,
	capability: string,
): TEntryPoint => {
	const entryPointWithCapability = {
		...entryPoint,
		getPreloadProps: (params: Parameters<TEntryPoint['getPreloadProps']>[0]) => {
			const preloadProps = entryPoint.getPreloadProps(params);

			return {
				...preloadProps,
				entryPoints: Object.fromEntries(
					Object.entries(preloadProps.entryPoints ?? {}).map(([key, nestedEntryPoint]) => [
						key,
						nestedEntryPoint == null
							? nestedEntryPoint
							: {
									...nestedEntryPoint,
									entryPoint:
										nestedEntryPoint.entryPoint == null
											? nestedEntryPoint.entryPoint
											: patchEntryPointCapability(nestedEntryPoint.entryPoint, capability),
								},
					]),
				),
				queries: Object.fromEntries(
					Object.entries(preloadProps.queries ?? {}).map(([key, query]) => [
						key,
						patchQueryCapability(query, capability),
					]),
				),
			};
		},
	};

	// The clone preserves the concrete EntryPoint shape while replacing getPreloadProps with the same signature.
	// oxlint-disable-next-line typescript/consistent-type-assertions
	return entryPointWithCapability as TEntryPoint;
};

export const withPreloadCapability = (
	entryPoint: RouteEntryPoint,
	capability: string,
): RouteEntryPoint => patchEntryPointCapability(entryPoint, capability);
